/**
 * <s-carousel> — a horizontally-scrollable "swim lane" that shows multiple items at once.
 *
 * UI-only: it wires the optional prev/next buttons to scroll the track by roughly one page
 * and toggles their `disabled` state at the start/end of the scroll range. The track is a
 * native scroll-snap container, so scrolling, touch/trackpad swiping, and keyboard focus
 * all work without JS — this component only enhances the arrow controls.
 *
 * Expected DOM (rendered by blocks/carousel.liquid):
 *   <s-carousel>
 *     [<button data-carousel-prev> / <button data-carousel-next>]
 *     <div data-carousel-track> …items… </div>
 *   </s-carousel>
 */
class SCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-carousel-track]");
    this.prevButton = this.querySelector("[data-carousel-prev]");
    this.nextButton = this.querySelector("[data-carousel-next]");

    if (!this.track) return;

    this.onPrev = () => this.scrollByPage(-1);
    this.onNext = () => this.scrollByPage(1);
    this.onScroll = () => this.updateControls();

    this.prevButton?.addEventListener("click", this.onPrev);
    this.nextButton?.addEventListener("click", this.onNext);
    this.track.addEventListener("scroll", this.onScroll, { passive: true });

    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.track);

    this.updateControls();

    if (this.hasAttribute("autoplay")) this.setupAutoplay();
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener("click", this.onPrev);
    this.nextButton?.removeEventListener("click", this.onNext);
    this.track?.removeEventListener("scroll", this.onScroll);
    this.resizeObserver?.disconnect();
    this.autoplayAbort?.abort();
    this.stopTimer();
  }

  /**
   * Automatic movement (opt-in with the `autoplay` attribute): moves one item every
   * `autoplay-interval` ms and loops back to the start. Pauses while a mouse hovers, while
   * keyboard focus is inside, while a finger is on it (resumes a few seconds later),
   * while the tab is hidden, and for good when the pause button is pressed.
   */
  setupAutoplay() {
    this.autoplayAbort = new AbortController();
    const opts = { signal: this.autoplayAbort.signal };
    this.interval = parseInt(this.getAttribute("autoplay-interval"), 10) || 3500;
    this.userPaused = false;
    this.holds = new Set();
    this.toggleButton = this.querySelector("[data-carousel-toggle]");

    this.toggleButton?.addEventListener("click", () => {
      this.userPaused = !this.userPaused;
      this.updateToggle();
      this.syncTimer();
    }, opts);

    this.addEventListener("pointerenter", (e) => { if (e.pointerType === "mouse") this.hold("hover"); }, opts);
    this.addEventListener("pointerleave", (e) => { if (e.pointerType === "mouse") this.release("hover"); }, opts);
    this.addEventListener("focusin", (e) => { if (e.target.matches?.(":focus-visible")) this.hold("focus"); }, opts);
    this.addEventListener("focusout", (e) => { if (!this.contains(e.relatedTarget)) this.release("focus"); }, opts);
    this.track.addEventListener("touchstart", () => {
      clearTimeout(this.touchTimer);
      this.hold("touch");
    }, { ...opts, passive: true });
    this.track.addEventListener("touchend", () => {
      clearTimeout(this.touchTimer);
      this.touchTimer = setTimeout(() => this.release("touch"), 4000);
    }, { ...opts, passive: true });
    document.addEventListener("visibilitychange", () => {
      document.hidden ? this.hold("hidden") : this.release("hidden");
    }, opts);

    this.updateToggle();
    this.syncTimer();
  }

  hold(reason) { this.holds.add(reason); this.syncTimer(); }
  release(reason) { this.holds.delete(reason); this.syncTimer(); }

  syncTimer() {
    const shouldRun = !this.userPaused && this.holds.size === 0;
    if (shouldRun && !this.timer) {
      this.timer = setInterval(() => this.step(), this.interval);
    } else if (!shouldRun) {
      this.stopTimer();
    }
  }

  stopTimer() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  /** Move one item along, or back to the start once the end is reached. */
  step() {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    if (maxScroll <= 1) return;
    const isRtl = getComputedStyle(this.track).direction === "rtl";
    const position = Math.abs(this.track.scrollLeft);
    if (position >= maxScroll - 1) {
      this.track.scrollTo({ left: 0, behavior: "smooth" });
      return;
    }
    const first = this.track.firstElementChild;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const amount = (first ? first.getBoundingClientRect().width : this.track.clientWidth / 2) + gap;
    this.track.scrollBy({ left: isRtl ? -amount : amount, behavior: "smooth" });
  }

  updateToggle() {
    if (!this.toggleButton) return;
    const paused = this.userPaused;
    this.toggleButton.setAttribute("aria-label", paused ? this.toggleButton.dataset.labelPlay : this.toggleButton.dataset.labelPause);
    const pauseIcon = this.toggleButton.querySelector("[data-icon-pause]");
    const playIcon = this.toggleButton.querySelector("[data-icon-play]");
    if (pauseIcon) pauseIcon.hidden = paused;
    if (playIcon) playIcon.hidden = !paused;
  }

  /**
   * Scroll the track by ~80% of its visible width in the given direction.
   * @param {number} direction -1 for previous, 1 for next.
   */
  scrollByPage(direction) {
    const amount = this.track.clientWidth * 0.8 * direction;
    this.track.scrollBy({ left: amount, behavior: "smooth" });
  }

  /** Disable prev/next when the track is at its respective end. */
  updateControls() {
    if (!this.prevButton && !this.nextButton) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const atStart = this.track.scrollLeft <= 1;
    const atEnd = this.track.scrollLeft >= maxScroll - 1;
    const overflowing = maxScroll > 1;

    if (this.prevButton) this.prevButton.disabled = !overflowing || atStart;
    if (this.nextButton) this.nextButton.disabled = !overflowing || atEnd;
  }
}

if (!customElements.get("s-carousel")) {
  customElements.define("s-carousel", SCarousel);
}
